import React, { Component, Fragment } from 'react'
import { observer } from 'mobx-react'
import i18n from 'i18next'
import * as R from 'ramda'

import SearchLayout from '../components/Layouts/Search'
import DataTable from '../components/DataTable'
import Checkbox from '../components/form/Checkbox'
import Button from '../UniformComponents/Button'
import Select from '../components/form/Select'
import ChooseIndustryModal from '../components/Modals/ChooseIndustryModal'
import { PageSwitchTab, tabOptions } from '../components/PageSwitchTab'
import ProductSearchStore from './ProductSearchStore'
import ProductEdit from './ProductEdit'
import { removeQueryParam, setQueryParam, getQueryParam } from '../utils/helpers'

import './product.scss'

const PRODUCT_PARAM = 'productId'
const INDUSTRY_PARAM = 'industryCode'

@observer
class ProductSearch extends Component {
    constructor(props) {
        super(props)
        this.hasOnlyVENU = props.store.client.industryVENU.hasOnlyVENU
        this.productSearchStore = new ProductSearchStore(props.store)
        this.productSearchStore.setColumns(this.handleOpenProductModal)
        const { hasOnlyVENU } = this.productSearchStore.industryVENU
        this.state = {
            showIndustryModal: false,
            showProductModal: false,
            productId: 0,
            industryCode: hasOnlyVENU ? 'VENU' : 'EV',
            hasMultiDivision: R.pathOr(false, ['userData', 'flags', 'isMultiDivision'], props.store),
        }
    }

    async componentDidMount() {
        const { store, location, history } = this.props
        await this.productSearchStore.initSearch()

        const { hasVENU, hasOnlyVENU } = this.productSearchStore.industryVENU
        // eslint-disable-next-line no-nested-ternary
        const eventSuffix = hasOnlyVENU ? '.only.VENU' : hasVENU ? '.and.VENU' : ''

        store.breadcrumps = [
            {
                name: 'event',
                url: '/event',
                title: i18n.t(`navmenu.eventStructure${eventSuffix}`),
            },
        ]

        this.checkQueryParam(location, history)
    }

    checkQueryParam = (location, history) => {
        const productId = getQueryParam(location, PRODUCT_PARAM)
        const industryCode = getQueryParam(location, INDUSTRY_PARAM)

        if (productId === -1 || (productId === 0 && industryCode === -1)) {
            removeQueryParam(history.location, history, PRODUCT_PARAM)
            removeQueryParam(history.location, history, INDUSTRY_PARAM)
            return
        }
        this.handleOpenProductModal(industryCode, productId)
    }

    toggleProductModal = () => {
        this.setState((prevState) => ({
            showProductModal: !prevState.showProductModal,
        }))
    }

    toggleIndustryModal = (showIndustryModal) => {
        this.setState({ showIndustryModal })
    }

    handleOpenProductModal = (industryCode, productId = 0) => {
        const { history } = this.props
        const isEditing = productId !== 0

        if (!isEditing) {
            setQueryParam(history.location, history, INDUSTRY_PARAM, industryCode)
            this.setState({ industryCode })
            this.toggleIndustryModal(false)
        }

        setQueryParam(history.location, history, PRODUCT_PARAM, productId)
        this.setState({ productId })

        this.toggleProductModal()
    }

    handleCloseProductModal = () => {
        const { history } = this.props
        const { hasOnlyVENU } = this.productSearchStore.industryVENU

        removeQueryParam(history.location, history, PRODUCT_PARAM)
        removeQueryParam(history.location, history, INDUSTRY_PARAM)
        this.setState({ productId: 0, industryCode: hasOnlyVENU ? 'VENU' : 'EV' })
        this.toggleProductModal()
        this.productSearchStore.initSearch()
    }

    handleNewProduct = () => {
        const { industries } = this.productSearchStore
        if (industries.length === 1) {
            this.handleOpenProductModal(industries[0].code)
        } else {
            this.toggleIndustryModal(true)
        }
    }

    renderButtons = () => {
        const { store } = this.props
        const renderAddEventBtn = store.userData.role < 140

        const { hasVENU, hasOnlyVENU } = this.productSearchStore.industryVENU
        // eslint-disable-next-line no-nested-ternary
        const label = i18n.t(hasOnlyVENU ? 'add_only_venue' : hasVENU ? 'add_event_or_venue' : 'add_event')

        return (
            <Fragment>
                {renderAddEventBtn && (
                    <Button
                        cssClass="primary large w-100"
                        onClick={this.handleNewProduct}
                        icon="fa-light fa-circle-plus"
                        label={label}
                    />
                )}
            </Fragment>
        )
    }

    renderFilterOptions = () => {
        const { hasVENU, hasOnlyVENU } = this.productSearchStore.industryVENU
        // eslint-disable-next-line no-nested-ternary
        const prefix = hasOnlyVENU ? 'venue' : hasVENU ? 'product_venue' : 'product'
        const filterName = `${prefix}_type`
        return (
            <Fragment>
                <br />

                <fieldset className="filter">
                    <Select
                        label={i18n.t('portfolio')}
                        options={this.productSearchStore.allPortfolios}
                        onBlur={this.productSearchStore.changePortfolio}
                        value={this.productSearchStore.filter.portfolio}
                    />
                </fieldset>
                <fieldset className="filter">
                    <legend className="legend-title">
                        {i18n.t(filterName)}
                    </legend>
                    <div>
                        {this.productSearchStore.searchProductTypes.map(({ value, label }) => (
                            <Checkbox
                                onBlur={this.productSearchStore.setProductTypeFilter}
                                name={value}
                                checked={this.productSearchStore.filter.productType[value]}
                                label={label}
                            />
                        ))}
                    </div>
                </fieldset>
            </Fragment>
        )
    }

    renderTablePanel = () => (
        <DataTable
            store={this.productSearchStore}
            inMemory
            dataSet={this.productSearchStore.filteredList || []}
            columns={this.productSearchStore.columns}
            sorting
            sortKey="name"
            sortDesc={1}
            cssClass="search-table"
        />
    )

    renderGuide = () => {
        const { industryVENU } = this.productSearchStore
        const desc = i18n.t(`productSearch.introText${industryVENU.hasOnlyVENU ? '.onlyVenues' : industryVENU.hasVENU ? '.venues' : ''}`)
        return <div dangerouslySetInnerHTML={{ __html: i18n.t(desc) }} />
    }

    render() {
        const {
            showProductModal, showIndustryModal, productId, industryCode, hasMultiDivision,
        } = this.state
        const { store, location, history } = this.props
        const { industryVENU } = this.productSearchStore
        // eslint-disable-next-line no-nested-ternary
        const pageTitle = i18n.t(industryVENU.hasOnlyVENU ? 'venues' : industryVENU.hasVENU ? 'venues_events' : 'events')

        return (
            <Fragment>
                <PageSwitchTab
                    appStore={store}
                    hasMultiDivision={hasMultiDivision}
                    location={location}
                    history={history}
                    tabOptions={tabOptions.manageEvents}
                    industryVENU={industryVENU}
                />
                <SearchLayout
                    pageTitle={pageTitle}
                    renderFilterOptions={this.renderFilterOptions}
                    renderTablePanel={this.renderTablePanel}
                    renderGuide={this.renderGuide}
                    renderButtons={this.renderButtons}
                    resetFilter={this.productSearchStore.resetFilter}
                    searchStore={this.productSearchStore}
                    hasTopNav
                />
                <ChooseIndustryModal
                    industryChooseOpen={showIndustryModal}
                    openCreateItem={this.handleOpenProductModal}
                    toggleIndustryChoose={this.toggleIndustryModal}
                    industries={this.productSearchStore.industries}
                />
                <ProductEdit
                    store={store}
                    showModal={showProductModal}
                    handleCloseModal={this.handleCloseProductModal}
                    productId={productId}
                    industryCode={industryCode}
                />
            </Fragment>
        )
    }
}

export default ProductSearch
